import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import '../product/ProductIndexPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

function buildIndexPayload(categories, selectedOrder) {
  const selectedSet = new Set(selectedOrder.map(String));
  const unselectedIds = categories
    .map((c) => c?._id ?? c?.id)
    .filter((id) => id != null && !selectedSet.has(String(id)));

  const orderedIds = [...selectedOrder, ...unselectedIds];
  return orderedIds.map((categoryId, index) => ({ categoryId, index }));
}

export default function CategoryIndexPage() {
  const navigate = useNavigate();
  const { loading, requestWithMeta } = useHttp();

  const [categories, setCategories] = React.useState([]);
  const [categoriesLoading, setCategoriesLoading] = React.useState(true);
  const [selectedOrder, setSelectedOrder] = React.useState([]);
  const [submitError, setSubmitError] = React.useState(null);

  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);

  const fetchCategories = React.useCallback(async () => {
    setCategoriesLoading(true);
    setSubmitError(null);
    try {
      const res = await requestWithMeta('/operator/categories', 'GET');
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить категории');
        return;
      }

      const list = Array.isArray(res?.data?.categories) ? res.data.categories : [];
      setCategories(list);
      setSelectedOrder([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const getOrderNumber = React.useCallback(
    (categoryId) => {
      const key = String(categoryId);
      const idx = selectedOrder.findIndex((id) => String(id) === key);
      return idx === -1 ? null : idx + 1;
    },
    [selectedOrder]
  );

  const toggleCategory = React.useCallback((categoryId) => {
    const key = String(categoryId);
    setSelectedOrder((prev) => {
      const idx = prev.findIndex((id) => String(id) === key);
      if (idx === -1) return [...prev, categoryId];
      return prev.filter((id) => String(id) !== key);
    });
  }, []);

  const selectedCount = selectedOrder.length;
  const isSubmitDisabled = loading || categoriesLoading || selectedCount === 0;

  async function handleSubmit() {
    setSubmitError(null);

    const payload = buildIndexPayload(categories, selectedOrder);
    if (payload.length === 0) {
      setSubmitError('Нет категорий для сохранения');
      return;
    }

    const res = await requestWithMeta('/operator/categories/index', 'PUT', {
      categories: payload,
    });

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить порядок');
      return;
    }

    navigate(-1);
  }

  return (
    <div className="shop-create-root product-index-page-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Порядок категорий"
          subtitle="Нажимайте на категории в нужном порядке. Повторное нажатие снимает номер."
          onBack={() => navigate(-1)}
          backClassName="product-index-page-backButton"
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <div className="product-index-page-listHeader">
              <div className="shop-create-label product-index-page-listTitle">Категории</div>
              <div className="shop-create-help shop-create-helpTight">
                {categoriesLoading
                  ? 'Загрузка…'
                  : selectedCount > 0
                    ? `Выбрано: ${selectedCount} из ${categories.length}`
                    : `${categories.length} категорий`}
              </div>
            </div>

            <div className="product-index-page-list">
              {categoriesLoading ? (
                skeletonItems.map((item) => (
                  <div key={item} className="product-index-page-item product-index-page-item--textOnly product-index-page-item--skeleton">
                    <div className="shop-create-skeleton shop-create-skeleton--text product-index-page-skeletonName" />
                    <div className="shop-create-skeleton product-index-page-skeletonBadge" />
                  </div>
                ))
              ) : categories.length === 0 ? (
                <div className="shop-create-help">Категории не найдены.</div>
              ) : (
                categories.map((category) => {
                  const categoryId = category?._id ?? category?.id;
                  const orderNumber = getOrderNumber(categoryId);
                  const isSelected = orderNumber != null;
                  const productCount =
                    category?.productCount ??
                    (Array.isArray(category?.productIds) ? category.productIds.length : 0);

                  return (
                    <button
                      key={categoryId}
                      type="button"
                      className={`product-index-page-item product-index-page-item--textOnly${isSelected ? ' product-index-page-item--selected' : ''}`}
                      onClick={() => toggleCategory(categoryId)}
                      aria-pressed={isSelected}
                      aria-label={
                        isSelected
                          ? `${category?.name || 'Категория'}, порядок ${orderNumber}. Нажмите, чтобы снять`
                          : `${category?.name || 'Категория'}. Нажмите, чтобы выбрать`
                      }
                    >
                      <div className="product-index-page-main">
                        <div className="product-index-page-name">{category?.name || 'Без названия'}</div>
                        <div className="product-index-page-meta">
                          {productCount} {productCount === 1 ? 'товар' : productCount >= 2 && productCount <= 4 ? 'товара' : 'товаров'}
                        </div>
                      </div>

                      <div
                        className={`product-index-page-orderBadge${isSelected ? ' product-index-page-orderBadge--filled' : ''}`}
                        aria-hidden="true"
                      >
                        {isSelected ? orderNumber : ''}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Сохранение…' : 'Сохранить порядок'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}
